import { ChangeDetectionStrategy, Component, ContentChild, ElementRef, Input, TemplateRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from "@ngx-translate/core";
import * as FlatModels from '@medsurf/flat-models';
import * as FlatFacades from '@medsurf/flat-facades';
import { MediaControlService } from '@medsurf/flat-services';

@Component({
    selector: 'medsurf-design-content-template-slide-tree',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './template-slide-tree.content.design.html',
    styleUrls: ['./template-slide-tree.content.design.scss'],
    standalone: false
})
export class TemplateSlideTreeContentDesign {
  @Input() name?: string;
  @Input() inputClass?: string;
  
  @ContentChild('override_menu_template') override_menu_template?: TemplateRef<ElementRef>;
  @ContentChild('override_slide_template') override_slide_template?: TemplateRef<ElementRef>;
  @ContentChild('override_slide_layers_template') override_slide_layers_template?: TemplateRef<ElementRef>;
  @ContentChild('override_slide_groups_template') override_slide_groups_template?: TemplateRef<ElementRef>;
  @ContentChild('override_slide_questions_template') override_slide_questions_template?: TemplateRef<ElementRef>;
  @ContentChild('override_layer_template') override_layer_template?: TemplateRef<ElementRef>;
  @ContentChild('override_image_template') override_image_template?: TemplateRef<ElementRef>;
  @ContentChild('override_image_annotations_template') override_image_annotations_template?: TemplateRef<ElementRef>;
  @ContentChild('override_image_pois_template') override_image_pois_template?: TemplateRef<ElementRef>;
  @ContentChild('override_annotation_template') override_annotation_template?: TemplateRef<ElementRef>;
  @ContentChild('override_group_template') override_group_template?: TemplateRef<ElementRef>;
  @ContentChild('override_image_poi_template') override_image_poi_template?: TemplateRef<ElementRef>;
  @ContentChild('override_question_template') override_question_template?: TemplateRef<ElementRef>;
  
  handled = false;
  typedSlideLayersExpanded: { [id: string]: boolean } = {};
  typedSlideAnnotationsExpanded: { [id: string]: boolean } = {};
  typedSlideGroupsExpanded: { [id: string]: boolean } = {};
  typedSlidePoisExpanded: { [id: string]: boolean } = {};
  typedSlideQuestionsExpanded: { [id: string]: boolean } = {};
  typedLayerExpanded: { [id: string]: boolean } = {};
  typedImageExpanded: { [id: string]: boolean } = {};
  typedImageAnnotationExpanded: { [id: string]: boolean } = {};
  typedImagePoiExpanded: { [id: string]: boolean } = {};
  typedGroupExpanded: { [id: string]: boolean } = {};
  typedPoiExpanded: { [id: string]: boolean } = {};
  typedPoiAnnotationExpanded: { [id: string]: boolean } = {};
  typedQuestionExpanded: { [id: string]: boolean } = {};
  readonly FlatModels = FlatModels;

  constructor(public alertControlFacade: FlatFacades.AlertControlFacade,
              public navigationControlFacade: FlatFacades.NavigationControlFacade,
              public nodeSharedFacade: FlatFacades.NodeSharedFacade,
              public slideAuthorFacade: FlatFacades.SlideAuthorFacade,
              public mediaControlService: MediaControlService,
              public translate: TranslateService) {
    this.slideAuthorFacade.typedSlideLayersExpanded$.pipe(takeUntilDestroyed()).subscribe((typedSlideLayersExpanded) => {
      this.typedSlideLayersExpanded = typedSlideLayersExpanded;
    });
    this.slideAuthorFacade.typedSlideAnnotationsExpanded$.pipe(takeUntilDestroyed()).subscribe((typedSlideAnnotationsExpanded) => {
      this.typedSlideAnnotationsExpanded = typedSlideAnnotationsExpanded;
    });
    this.slideAuthorFacade.typedSlideGroupsExpanded$.pipe(takeUntilDestroyed()).subscribe((typedSlideGroupsExpanded) => {
      this.typedSlideGroupsExpanded = typedSlideGroupsExpanded;
    });
    this.slideAuthorFacade.typedSlidePoisExpanded$.pipe(takeUntilDestroyed()).subscribe((typedSlidePoisExpanded) => {
      this.typedSlidePoisExpanded = typedSlidePoisExpanded;
    });
    this.slideAuthorFacade.typedSlideQuestionsExpanded$.pipe(takeUntilDestroyed()).subscribe((typedSlideQuestionsExpanded) => {
      this.typedSlideQuestionsExpanded = typedSlideQuestionsExpanded;
    });
    this.slideAuthorFacade.typedLayerExpanded$.pipe(takeUntilDestroyed()).subscribe((typedLayerExpanded) => {
      this.typedLayerExpanded = typedLayerExpanded;
    });
    this.slideAuthorFacade.typedImageExpanded$.pipe(takeUntilDestroyed()).subscribe((typedImageExpanded) => {
      this.typedImageExpanded = typedImageExpanded;
    });
    this.slideAuthorFacade.typedImageAnnotationExpanded$.pipe(takeUntilDestroyed()).subscribe((typedImageAnnotationExpanded) => {
      this.typedImageAnnotationExpanded = typedImageAnnotationExpanded;
    });
    this.slideAuthorFacade.typedImagePoiExpanded$.pipe(takeUntilDestroyed()).subscribe((typedImagePoiExpanded) => {
      this.typedImagePoiExpanded = typedImagePoiExpanded;
    });
    this.slideAuthorFacade.typedGroupExpanded$.pipe(takeUntilDestroyed()).subscribe((typedGroupExpanded) => {
      this.typedGroupExpanded = typedGroupExpanded;
    });
    this.slideAuthorFacade.typedPoiExpanded$.pipe(takeUntilDestroyed()).subscribe((typedPoiExpanded) => {
      this.typedPoiExpanded = typedPoiExpanded;
    });
    this.slideAuthorFacade.typedPoiAnnotationExpanded$.pipe(takeUntilDestroyed()).subscribe((typedPoiAnnotationExpanded) => {
      this.typedPoiAnnotationExpanded = typedPoiAnnotationExpanded;
    });
    this.slideAuthorFacade.typedQuestionExpanded$.pipe(takeUntilDestroyed()).subscribe((typedQuestionExpanded) => {
      this.typedQuestionExpanded = typedQuestionExpanded;
    });
  }

  handleEvent() {
    this.handled = true;
  }
  
  // <editor-fold desc="Slide Layers">

  onSlideLayersExpand(page: FlatModels.PageEntityModels.PageEntityType,
                      override?: boolean) {
    this.typedSlideLayersExpanded[page.id] = override ? override : !this.typedSlideLayersExpanded[page.id];
    this.slideAuthorFacade.requestSetTypedSlideLayersExpanded(this.typedSlideLayersExpanded);
  }
  
  //</editor-fold>
  
  // <editor-fold desc="Slide Groups">

  onSlideGroupsExpand(page: FlatModels.PageEntityModels.PageEntityType,
                      override?: boolean) {
    this.typedSlideGroupsExpanded[page.id] = override ? override : !this.typedSlideGroupsExpanded[page.id];
    this.slideAuthorFacade.requestSetTypedSlideGroupsExpanded(this.typedSlideGroupsExpanded);
  }
  
  //</editor-fold>
  
  // <editor-fold desc="Slide Questions">

  onSlideQuestionsExpand(page: FlatModels.PageEntityModels.PageEntityType,
                         override?: boolean) {
    this.typedSlideQuestionsExpanded[page.id] = override ? override : !this.typedSlideQuestionsExpanded[page.id];
    this.slideAuthorFacade.requestSetTypedSlideQuestionsExpanded(this.typedSlideQuestionsExpanded);
  }
  
  //</editor-fold>
  
  // <editor-fold desc="Layer">

  trackByLayer(index: number,
               item: FlatModels.LayerEntityModels.Layer) {
    return item.id + item.position || index;
  }

  onLayerExpand(layer: FlatModels.LayerEntityModels.Layer,
                override?: boolean) {
    this.handleEvent();
    this.typedLayerExpanded[layer.id] = override ? override : !this.typedLayerExpanded[layer.id];
    this.slideAuthorFacade.requestSetTypedLayerExpanded(this.typedLayerExpanded);
  }

  //</editor-fold>

  // <editor-fold desc="Image">

  trackByImage(index: number,
               item: FlatModels.ImageEntityModels.Image) {
    return item.id + item.sequenceNumber || index;
  }

  onImageExpand(image: FlatModels.ImageEntityModels.Image,
                override?: boolean) {
    this.handleEvent();
    this.typedImageExpanded[image.id] = override ? override : !this.typedImageExpanded[image.id];
    this.slideAuthorFacade.requestSetTypedImageExpanded(this.typedImageExpanded);
  }
  
  onImageAnnotationsExpand(image: FlatModels.ImageEntityModels.Image,
                           override?: boolean) {
    this.typedImageAnnotationExpanded[image.id] = override ? override : !this.typedImageAnnotationExpanded[image.id];
    this.slideAuthorFacade.requestSetTypedImageAnnotationExpanded(this.typedImageAnnotationExpanded);
  }

  onImagePoisExpand(image: FlatModels.ImageEntityModels.Image,
                    override?: boolean) {
    this.typedImagePoiExpanded[image.id] = override ? override : !this.typedImagePoiExpanded[image.id];
    this.slideAuthorFacade.requestSetTypedImagePoiExpanded(this.typedImagePoiExpanded);
  }
  
  //</editor-fold>

  // <editor-fold desc="Annotation">

  trackByAnnotation(index: number,
                    item: FlatModels.AnnotationEntityModels.AnnotationEntityType) {
    return item.id || index;
  }

  //</editor-fold>
  
  // <editor-fold desc="Group">

  trackByGroup(index: number,
               item: FlatModels.GroupEntityModels.Group) {
    return item.id + item.order || index;
  }

  onGroupExpand(group: FlatModels.GroupEntityModels.Group,
                override?: boolean) {
    this.handleEvent();
    this.typedGroupExpanded[group.id] = override ? override : !this.typedGroupExpanded[group.id];
    this.slideAuthorFacade.requestSetTypedGroupExpanded(this.typedGroupExpanded);
  }

  //</editor-fold>
  
  // <editor-fold desc="Poi">

  trackByPoi(index: number,
             item: FlatModels.PointOfInterestEntityModels.PointOfInterest) {
    return item.id + item.order || index;
  }

  onPoiExpand(poi: FlatModels.PointOfInterestEntityModels.PointOfInterest,
              override?: boolean) {
    this.handleEvent();
    this.typedPoiExpanded[poi.id] = override ? override : !this.typedPoiExpanded[poi.id];
    this.slideAuthorFacade.requestSetTypedPoiExpanded(this.typedPoiExpanded);
  }
  
  //</editor-fold>
  
  // <editor-fold desc="Question">

  trackByQuestion(index: number,
                  item: FlatModels.QuestionEntityModels.QuestionEntityType) {
    return item.id + item.order || index;
  }
  
  //</editor-fold>
}
